import * as React from 'react'

// constate(useCounter, value => value.count)
//                      ^^^^^^^^^^^^^^^^^^^^
type Selector<Value> = (value: Value) => any

// const [Provider, useCount, useIncrement] = constate(...)
//                  ^^^^^^^^^^^^^^^^^^^^^^
type SelectorHooks<Selectors> = {
  [K in keyof Selectors]: () => Selectors[K] extends (...args: any) => infer R ? R : never
}

// const [Provider, useCounterContext] = constate(...)
// or               ^^^^^^^^^^^^^^^^^
// const [Provider, useCount, useIncrement] = constate(...)
//                  ^^^^^^^^^^^^^^^^^^^^^^
type Hooks<Value, Selectors extends Selector<Value>[]> = Selectors['length'] extends 0
  ? [() => Value]
  : SelectorHooks<Selectors>

// const [Provider, useContextValue] = constate(useValue)
//       ^^^^^^^^^^^^^^^^^^^^^^^^^^^
type ConstateTuple<Props, Value, Selectors extends Selector<Value>[]> = [
  React.FC<React.PropsWithChildren<Props>>,
  ...Hooks<Value, Selectors>,
]

const isDev = !!import.meta.env.DEV

const NO_PROVIDER = undefined

function createUseContext(context: React.Context<any>): any {
  return () => {
    const value = React.useContext(context)
    if (isDev && value === undefined) {
      const warnMessage = context.displayName
        ? `The context consumer of ${context.displayName} must be wrapped with its corresponding Provider`
        : 'Component must be wrapped with Provider.'
      // eslint-disable-next-line no-console
      console.warn(warnMessage)
    }
    return value
  }
}

/**
 * React Context + State
 *
 * @info this is a fork from {@link https://github.com/diegohaz/constate}
 *
 * @note please provide unique ctxName for better debugging
 */
function constate<Props, Value, Selectors extends Selector<Value>[]>(
  ctxName: string,
  useValue: (props: Props) => Value,
  ...selectors: Selectors
): ConstateTuple<Props, Value, Selectors> {
  // context pool
  let contexts = [] as React.Context<any>[]

  // hooks pool
  let hooks = [] as unknown as Hooks<Value, Selectors>

  /**
   * create context factory
   */
  function createContext(displayName: string) {
    const context = React.createContext(NO_PROVIDER)
    context.displayName = displayName || ctxName
    contexts.push(context)
    hooks.push(createUseContext(context))
  }

  // generate context
  if (selectors.length) {
    selectors.forEach((selector) => createContext(selector.name))
  } else {
    createContext(useValue.name)
  }

  /**
   * Provider factory.
   *
   * @note Provider is memoized to prevent unnecessary re-renders when parent components update.
   * The provider will still re-render when its props change, which is the desired behavior.
   */
  const GeneratedProvider: React.FC<React.PropsWithChildren<Props>> = React.memo(({ children, ...props }) => {
    const value = useValue(props as Props)
    let element = children as React.ReactElement
    for (let i = 0; i < contexts.length; i += 1) {
      const context = contexts[i]
      const selector = selectors[i] || ((v) => v)
      element = <context.Provider value={selector(value)}>{element}</context.Provider>
    }
    return element
  })

  if (isDev && useValue.name) {
    const contextName = useValue.name ? useValue.name.replace('use', '') : ctxName.replace('context', '')
    GeneratedProvider.displayName = contextName + 'ContextProvider'
  }

  return [GeneratedProvider, ...hooks]
}

export default constate
